import { Outlet, Link, useLocation } from 'react-router';
import { motion, AnimatePresence } from 'framer-motion';
import { useState, useMemo, useEffect } from 'react';
import { useAuth } from '../lib/auth.js';
import { formatDateOnly } from '../lib/dateUtils.js';
import type { AppSection } from '@gng-ehs/shared';

interface NavItem {
  path: string;
  label: string;
  icon: string;
  section: AppSection;
}

// Primary workflow navigation items
const MAIN_NAV_ITEMS: NavItem[] = [
  { path: 'dashboard', label: 'Dashboard', icon: '📊', section: 'dashboard' },
  { path: 'training', label: 'Training', icon: '🎓', section: 'training' },
  { path: 'form9', label: 'Form 9', icon: '🛗', section: 'form9' },
  { path: 'form10', label: 'Form 10', icon: '📋', section: 'form10' },
  { path: 'form11', label: 'Form 11', icon: '🧪', section: 'form11' },
  { path: 'renewals', label: 'Renewals', icon: '🔄', section: 'renewals' },
];

// Togglable Directory & History section items
const DIRECTORY_NAV_ITEMS: NavItem[] = [
  { path: 'companies', label: 'Companies', icon: '🏭', section: 'companies' },
  { path: 'equipment', label: 'Equipment', icon: '⚙️', section: 'equipment' },
  { path: 'personnel', label: 'Personnel', icon: '👤', section: 'personnel' },
  { path: 'email-history', label: 'Email History', icon: '✉️', section: 'renewals' },
];

function isPathActive(pathname: string, targetPath: string): boolean {
  return pathname === `/${targetPath}` || pathname.startsWith(`/${targetPath}/`) || pathname.endsWith(targetPath);
}

function getHeaderTitle(pathname: string): string {
  const last = pathname.split('/').filter(Boolean).pop() || 'Dashboard';
  if (last === 'email-history') return 'Email History';
  if (last === 'form9') return 'Form 9';
  if (last === 'form10') return 'Form 10';
  if (last === 'form11') return 'Form 11';
  return last.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

interface NavLinksProps {
  mainItems: NavItem[];
  directoryItems: NavItem[];
  directoryOpen: boolean;
  onToggleDirectory: () => void;
  isDirectoryChildActive: boolean;
  pathname: string;
  onItemClick?: () => void;
}

function NavLinks({
  mainItems,
  directoryItems,
  directoryOpen,
  onToggleDirectory,
  isDirectoryChildActive,
  pathname,
  onItemClick,
}: NavLinksProps) {
  return (
    <div className="space-y-1">
      {/* Main navigation items */}
      {mainItems.map((item) => {
        const active = isPathActive(pathname, item.path);
        return (
          <Link
            key={item.path}
            to={item.path}
            onClick={onItemClick}
            className={`flex items-center justify-between px-3 py-2 rounded-lg text-sm transition-colors ${
              active
                ? 'bg-brand-600 text-white font-medium shadow-sm'
                : 'text-slate-300 hover:bg-slate-800 hover:text-white'
            }`}
          >
            <div className="flex items-center gap-3">
              <span className="text-base">{item.icon}</span>
              <span>{item.label}</span>
            </div>
          </Link>
        );
      })}

      {/* Togglable Console section */}
      {directoryItems.length > 0 && (
        <div className="pt-1">
          <button
            type="button"
            onClick={onToggleDirectory}
            aria-expanded={directoryOpen}
            className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm transition-colors cursor-pointer select-none ${
              !directoryOpen && isDirectoryChildActive
                ? 'bg-slate-800 text-brand-300 font-medium'
                : 'text-slate-300 hover:bg-slate-800 hover:text-white'
            }`}
          >
            <div className="flex items-center gap-3">
              <span className="w-5 h-5 flex items-center justify-center shrink-0">
                <svg
                  className="w-4.5 h-4.5"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <rect x="2" y="4" width="20" height="16" rx="2.5" />
                  <path d="M6.5 9.5L9.5 12.5L6.5 15.5" />
                  <path d="M12 15.5h5" />
                </svg>
              </span>
              <span className="font-medium">Console</span>
            </div>
            <div className="flex items-center gap-1.5">
              {!directoryOpen && isDirectoryChildActive && (
                <span className="w-1.5 h-1.5 rounded-full bg-brand-400" />
              )}
              <svg
                className={`w-4 h-4 text-slate-400 transition-transform duration-200 ${
                  directoryOpen ? 'rotate-180' : ''
                }`}
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M19 9l-7 7-7-7" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
          </button>

          <AnimatePresence initial={false}>
            {directoryOpen && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.2, ease: 'easeInOut' }}
                className="overflow-hidden"
              >
                <div className="ml-3 pl-2.5 border-l border-slate-700/60 space-y-0.5 my-1">
                  {directoryItems.map((item) => {
                    const active = isPathActive(pathname, item.path);
                    return (
                      <Link
                        key={item.path}
                        to={item.path}
                        onClick={onItemClick}
                        className={`flex items-center gap-2.5 px-2.5 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                          active
                            ? 'bg-brand-600 text-white shadow-sm'
                            : 'text-slate-300 hover:text-white hover:bg-slate-800/80'
                        }`}
                      >
                        <span className="text-sm">{item.icon}</span>
                        <span>{item.label}</span>
                      </Link>
                    );
                  })}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      )}
    </div>
  );
}

export function MainLayout() {
  const { user, appUser, isAdmin, canRead, signOut } = useAuth();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);

  // Filter items based on user permissions
  const mainItems = useMemo(() => MAIN_NAV_ITEMS.filter((item) => canRead(item.section)), [canRead]);
  const directoryItems = useMemo(() => DIRECTORY_NAV_ITEMS.filter((item) => canRead(item.section)), [canRead]);

  // Check if any directory child is currently active
  const isDirectoryChildActive = useMemo(() => {
    return directoryItems.some((item) => isPathActive(location.pathname, item.path));
  }, [directoryItems, location.pathname]);

  // Persist toggled state in localStorage
  const [directoryOpen, setDirectoryOpen] = useState<boolean>(() => {
    try {
      const saved = localStorage.getItem('gng_nav_directory_open');
      return saved !== null ? saved === 'true' : true;
    } catch {
      return true;
    }
  });

  // Auto-expand section if user navigates to any of its children
  useEffect(() => {
    if (isDirectoryChildActive) {
      setDirectoryOpen(true);
    }
  }, [isDirectoryChildActive]);

  const handleToggleDirectory = () => {
    setDirectoryOpen((prev) => {
      const next = !prev;
      try {
        localStorage.setItem('gng_nav_directory_open', String(next));
      } catch {
        // Ignore storage access errors
      }
      return next;
    });
  };

  return (
    <div className="h-screen w-full flex bg-gray-50 overflow-hidden">
      {/* ── Desktop Sidebar ─────────────────── */}
      <aside className="hidden lg:flex w-60 flex-shrink-0 bg-slate-900 text-white flex-col">
        <div className="px-5 py-4 border-b border-slate-700">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-white flex items-center justify-center p-1 shadow-sm shrink-0">
              <img
                src="/cert_assets/gg_symbol.png"
                alt="G&G Logo"
                className="w-full h-full object-contain"
              />
            </div>
            <div className="min-w-0">
              <span className="font-semibold text-base tracking-tight block leading-tight text-white">G&G EHS</span>
              <p className="text-xs text-slate-400 mt-0.5">Compliance Portal</p>
            </div>
          </div>
        </div>

        <nav className="flex-1 py-3 px-3 overflow-y-auto">
          <NavLinks
            mainItems={mainItems}
            directoryItems={directoryItems}
            directoryOpen={directoryOpen}
            onToggleDirectory={handleToggleDirectory}
            isDirectoryChildActive={isDirectoryChildActive}
            pathname={location.pathname}
          />
        </nav>

        {/* ── Bottom pinned links: Trash & Admin Management ── */}
        {(canRead('trash') || isAdmin) && (
          <div className="px-3 py-2 border-t border-slate-700/80 space-y-1">
            {canRead('trash') && (
              <Link
                to="/trash"
                className={`flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors ${
                  isPathActive(location.pathname, 'trash')
                    ? 'bg-brand-600 text-white font-medium shadow-sm'
                    : 'text-slate-300 hover:bg-slate-800 hover:text-white'
                }`}
              >
                <span className="text-base">🗑️</span>
                <span>Trash</span>
              </Link>
            )}
            {isAdmin && (
              <Link
                to="/admin"
                className={`flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors ${
                  isPathActive(location.pathname, 'admin')
                    ? 'bg-brand-600 text-white font-medium shadow-sm'
                    : 'text-slate-300 hover:bg-slate-800 hover:text-white'
                }`}
              >
                <span className="text-base">🛡️</span>
                <span>Admin Management</span>
              </Link>
            )}
          </div>
        )}

        {/* ── Logged in user footer ── */}
        <div className="border-t border-slate-700 px-3 py-3">
          <div className="flex items-center gap-3 px-3 py-2">
            <div className="w-8 h-8 rounded-full bg-brand-600 flex items-center justify-center text-xs font-bold">
              {user?.email?.[0]?.toUpperCase() ?? 'U'}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate">{appUser?.full_name || user?.email}</p>
              {appUser?.role && (
                <p className="text-[11px] text-slate-400 capitalize">{appUser.role.replace('_', ' ')}</p>
              )}
            </div>
          </div>
          <button
            onClick={signOut}
            className="w-full mt-2 flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-sm text-slate-300 hover:bg-slate-800 transition-colors cursor-pointer"
          >
            Sign Out
          </button>
        </div>
      </aside>

      {/* ── Mobile drawer overlay ────────────── */}
      <AnimatePresence>
        {menuOpen && (
          <>
            <motion.div
              className="fixed inset-0 bg-black/50 z-40 lg:hidden"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setMenuOpen(false)}
            />
            <motion.aside
              className="fixed top-0 left-0 h-full w-72 bg-slate-900 text-white z-50 lg:hidden flex flex-col"
              initial={{ x: -288 }}
              animate={{ x: 0 }}
              exit={{ x: -288 }}
              transition={{ type: 'spring', damping: 25, stiffness: 200 }}
            >
              <div className="px-5 py-4 border-b border-slate-700 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-xl bg-white flex items-center justify-center p-1 shadow-sm shrink-0">
                    <img
                      src="/cert_assets/gg_symbol.png"
                      alt="G&G Logo"
                      className="w-full h-full object-contain"
                    />
                  </div>
                  <div className="min-w-0">
                    <span className="font-semibold text-base tracking-tight block leading-tight text-white">G&G EHS</span>
                    <p className="text-xs text-slate-400 mt-0.5">Compliance Portal</p>
                  </div>
                </div>
                <button
                  onClick={() => setMenuOpen(false)}
                  className="p-1.5 rounded-lg hover:bg-slate-800 text-slate-400 hover:text-white transition-colors cursor-pointer"
                >
                  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M18 6L6 18M6 6l12 12" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </button>
              </div>

              <nav className="flex-1 py-3 px-3 overflow-y-auto">
                <NavLinks
                  mainItems={mainItems}
                  directoryItems={directoryItems}
                  directoryOpen={directoryOpen}
                  onToggleDirectory={handleToggleDirectory}
                  isDirectoryChildActive={isDirectoryChildActive}
                  pathname={location.pathname}
                  onItemClick={() => setMenuOpen(false)}
                />
              </nav>

              {/* ── Mobile Bottom pinned links: Trash & Admin Management ── */}
              {(canRead('trash') || isAdmin) && (
                <div className="px-3 py-2 border-t border-slate-700/80 space-y-1">
                  {canRead('trash') && (
                    <Link
                      to="/trash"
                      onClick={() => setMenuOpen(false)}
                      className={`flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors ${
                        isPathActive(location.pathname, 'trash')
                          ? 'bg-brand-600 text-white font-medium shadow-sm'
                          : 'text-slate-300 hover:bg-slate-800 hover:text-white'
                      }`}
                    >
                      <span className="text-base">🗑️</span>
                      <span>Trash</span>
                    </Link>
                  )}
                  {isAdmin && (
                    <Link
                      to="/admin"
                      onClick={() => setMenuOpen(false)}
                      className={`flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors ${
                        isPathActive(location.pathname, 'admin')
                          ? 'bg-brand-600 text-white font-medium shadow-sm'
                          : 'text-slate-300 hover:bg-slate-800 hover:text-white'
                      }`}
                    >
                      <span className="text-base">🛡️</span>
                      <span>Admin Management</span>
                    </Link>
                  )}
                </div>
              )}

              {/* ── Logged in user footer ── */}
              <div className="border-t border-slate-700 px-3 py-3">
                <div className="flex items-center gap-3 px-3 py-2">
                  <div className="w-8 h-8 rounded-full bg-brand-600 flex items-center justify-center text-xs font-bold">
                    {user?.email?.[0]?.toUpperCase() ?? 'U'}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{appUser?.full_name || user?.email}</p>
                    {appUser?.role && (
                      <p className="text-[11px] text-slate-400 capitalize">{appUser.role.replace('_', ' ')}</p>
                    )}
                  </div>
                </div>
                <button
                  onClick={() => { signOut(); setMenuOpen(false); }}
                  className="w-full mt-2 flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-sm text-slate-300 hover:bg-slate-800 transition-colors cursor-pointer"
                >
                  Sign Out
                </button>
              </div>
            </motion.aside>
          </>
        )}
      </AnimatePresence>

      {/* ── Main content ────────────────────── */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* Top bar */}
        <header className="bg-slate-900 border-b border-slate-800 text-white px-5 py-3 flex items-center justify-between shadow-sm">
          <div className="flex items-center gap-3">
            {/* Mobile hamburger */}
            <button
              onClick={() => setMenuOpen(true)}
              className="lg:hidden p-1 rounded-md text-slate-300 hover:bg-slate-800 cursor-pointer"
            >
              <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M3 12h18M3 6h18M3 18h18" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
            <h1 className="text-base sm:text-lg font-semibold text-white">
              {getHeaderTitle(location.pathname)}
            </h1>
          </div>
          <div className="hidden sm:block text-xs sm:text-sm text-slate-400 font-medium">
            {new Date().toLocaleDateString('en-IN', { weekday: 'short' })}, {formatDateOnly(new Date().toISOString())}
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 flex flex-col min-h-0 overflow-hidden p-3 sm:p-4 md:p-6 bg-gray-50">
          <AnimatePresence mode="wait">
            <motion.div
              key={location.pathname}
              className="flex-1 flex flex-col min-h-0 overflow-hidden"
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.2 }}
            >
              <Outlet />
            </motion.div>
          </AnimatePresence>
        </main>

        {/* ── Mobile bottom nav ─────────────── */}
        <nav className="lg:hidden bg-white border-t border-gray-200 flex items-center justify-around py-2 pb-safe">
          {mainItems.slice(0, 5).map((item) => {
            const active = isPathActive(location.pathname, item.path);
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`flex flex-col items-center gap-1 px-2 py-1 rounded-lg text-xs ${
                  active ? 'text-brand-600 font-semibold' : 'text-gray-400'
                }`}
              >
                <span className="text-lg">{item.icon}</span>
                <span className="text-[10px] leading-tight">{item.label}</span>
              </Link>
            );
          })}
          {/* More menu to open full drawer */}
          <button
            type="button"
            onClick={() => setMenuOpen(true)}
            className={`flex flex-col items-center gap-1 px-2 py-1 rounded-lg text-xs cursor-pointer ${
              isDirectoryChildActive || isPathActive(location.pathname, 'trash') || (isAdmin && (location.pathname === '/admin' || location.pathname.endsWith('/admin')))
                ? 'text-brand-600 font-semibold'
                : 'text-gray-400 hover:text-gray-600'
            }`}
          >
            <span className="text-lg">☰</span>
            <span className="text-[10px] leading-tight">More</span>
          </button>
        </nav>
      </div>
    </div>
  );
}

